<script setup lang="ts">
import { AlertTriangle, LoaderCircle, X } from "lucide-vue-next"

withDefaults(
  defineProps<{
    open: boolean
    title: string
    description: string
    eyebrow?: string
    detail?: string
    confirmLabel?: string
    busy?: boolean
    danger?: boolean
    inputLabel?: string
    inputValue?: string
    inputType?: "text" | "number"
    inputHint?: string
  }>(),
  {
    eyebrow: "Confirm action",
    detail: "",
    confirmLabel: "确认",
    busy: false,
    danger: false,
    inputLabel: "",
    inputValue: "",
    inputType: "text",
    inputHint: "",
  },
)

const emit = defineEmits<{
  close: []
  confirm: []
  "update:inputValue": [value: string]
}>()
</script>

<template>
  <Teleport to="body">
    <Transition name="action-dialog">
      <div v-if="open" class="modal-backdrop" @click.self="!busy && emit('close')">
        <form class="modal action-dialog" role="dialog" aria-modal="true" @submit.prevent="emit('confirm')">
          <div class="modal__heading">
            <div>
              <span class="eyebrow">{{ eyebrow }}</span>
              <h2>{{ title }}</h2>
              <p>{{ description }}</p>
            </div>
            <button type="button" class="icon-button" aria-label="关闭" :disabled="busy" @click="emit('close')">
              <X :size="18" />
            </button>
          </div>

          <div class="action-dialog__body">
            <div v-if="detail" class="action-dialog__detail">
              <span class="action-dialog__icon" :class="{ 'action-dialog__icon--danger': danger }">
                <AlertTriangle :size="18" />
              </span>
              <strong>{{ detail }}</strong>
            </div>
            <label v-if="inputLabel" class="field">
              <span>{{ inputLabel }}</span>
              <input
                :value="inputValue"
                :type="inputType"
                autofocus
                @input="emit('update:inputValue', ($event.target as HTMLInputElement).value)"
              />
              <small v-if="inputHint">{{ inputHint }}</small>
            </label>
          </div>

          <div class="modal__actions">
            <button type="button" class="secondary-button" :disabled="busy" @click="emit('close')">取消</button>
            <button class="primary-button" :class="{ 'action-dialog__danger': danger }" :disabled="busy">
              <LoaderCircle v-if="busy" class="spin" :size="15" />
              {{ confirmLabel }}
            </button>
          </div>
        </form>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.action-dialog { width: min(470px, 100%); }
.action-dialog__body { display: grid; gap: 16px; }
.action-dialog__detail { display: flex; align-items: center; gap: 12px; padding: 14px; border: 1px solid var(--line); border-radius: 11px; background: var(--panel); }
.action-dialog__detail strong { min-width: 0; overflow: hidden; font-size: 13px; text-overflow: ellipsis; white-space: nowrap; }
.action-dialog__icon { width: 36px; height: 36px; display: grid; flex: 0 0 auto; place-items: center; border-radius: 9px; color: var(--warning); background: color-mix(in oklch, var(--warning) 12%, transparent); }
.action-dialog__icon--danger { color: var(--accent); background: var(--accent-soft); }
.action-dialog__danger { border-color: var(--accent); background: var(--accent); }
.action-dialog-enter-active, .action-dialog-leave-active { transition: opacity 140ms ease; }
.action-dialog-enter-from, .action-dialog-leave-to { opacity: 0; }
</style>
